import { useEffect, useRef, useState } from "react";
import { FaUserCircle, FaRobot } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaSpinner } from "react-icons/fa";

const API_URL = process.env.REACT_APP_CHAT_API_URL || "https://api.nexagpt.ai/api/chat";
const WEBSITE_ID = process.env.REACT_APP_CHAT_WEBSITE_ID || "";

const ALL_SUGGESTIONS = [
  "What is NexaGPT?",
  "How does NexaGPT work?",
  "What data sources can I train on?",
  "Can I train on PDF files?",
  "Does NexaGPT support Notion?",
  "Can I integrate with HubSpot?",
  "How do I embed the chatbot on my site?",
  "Is there a free trial?",
  "What languages does NexaGPT support?",
  "How does lead capture work?",
  "Can I customize the chatbot appearance?",
  "Does NexaGPT support YouTube videos?",
  "How do I connect Zendesk?",
  "What analytics are available?",
  "Is my data secure?",
  "Can I use NexaGPT for customer support?",
  "How many chatbots can I create?",
  "Does NexaGPT support Confluence?",
  "How does CRM integration work?",
  "What is the Enterprise plan?",
];

// Shuffle array utility
function shuffleArray(arr) {
  const array = [...arr];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// Parse numbered-step responses
function parseStepsResponse(text) {
  const stepParts = text.split(/\d+\.\s+/).filter(Boolean);
  const steps = [];
  let support = null;

  stepParts.forEach((part) => {
    const supportIndex = part.search(/If you encounter|contact support/);
    if (supportIndex !== -1) {
      support = part.slice(supportIndex).trim();
      steps.push(part.slice(0, supportIndex).trim());
    } else {
      steps.push(part.trim());
    }
  });

  return { steps, support };
}

// Format message timestamp
const getTimeLabel = (timestamp) => {
  const now = new Date();
  const diff = Math.floor((now - timestamp) / 60000); // in minutes
  if (diff < 1) return "Just now";
  if (diff < 60) return `${diff} min${diff > 1 ? "s" : ""} ago`;
  const hours = Math.floor(diff / 60);
  return `${hours} hour${hours > 1 ? "s" : ""} ago`;
};

export default function ChatBox() {
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    setSuggestions(shuffleArray(ALL_SUGGESTIONS));
  }, []);

  // Scroll ~80% from top
  useEffect(() => {
    const container = bottomRef.current?.parentElement;
    if (container) {
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      container.scrollTo({
        top: scrollHeight - clientHeight * 0.5,
        behavior: "smooth",
      });
    }
  }, [messages, loading]);

  // Typing effect
  const typeMessage = (fullText, onUpdate, onComplete) => {
    let index = 0;
    setTyping(true);
    const interval = setInterval(() => {
      index++;
      onUpdate(fullText.slice(0, index));
      if (index === fullText.length) {
        clearInterval(interval);
        setTyping(false);
        onComplete();
      }
    }, 20);
  };

  // Send message
  const sendMessage = async (text) => {
    const messageText = text ?? input;
    if (!messageText.trim() || loading) return;

    if (!started) setStarted(true);

    // Add user message
    setMessages((prev) => [
      ...prev,
      { role: "user", content: messageText, timestamp: new Date() },
    ]);
    setInput("");
    setLoading(true);

    if (text) setSuggestions((prev) => prev.filter((s) => s !== text));

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: messageText,
          websiteId: WEBSITE_ID,
        }),
      });

      const data = await res.json();
      const content = data.answer;

      // Add placeholder assistant message
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "",
          parsedSteps: null,
          timestamp: new Date(),
        },
      ]);
      setLoading(false);

      // Type assistant message
      typeMessage(
        content,
        (typedText) => {
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1].content = typedText;
            return updated;
          });
        },
        () => setLoading(false)
      );
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "⚠️ Something went wrong.",
          timestamp: new Date(),
        },
      ]);
      setTyping(false);
      setLoading(false);
    }
  };

  const currentSuggestions = suggestions.slice(0, 4);

  if (!started) {
    return (
      <div className="h-screen bg-gray-100 flex flex-col items-center justify-center px-6">
        <div className="max-w-3xl w-full">
          <p className="text-sm text-gray-500 mb-2">✨ Hi Sakthivel</p>
          <h1 className="text-2xl font-semibold mb-8 text-gray-900">
            Ask NexaGPT Assistant
          </h1>

          <div className="ai-input mb-6">
            <div className="flex items-center bg-white rounded-full px-4 py-3 shadow">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask anything about NexaGPT..."
                className="flex-1 bg-transparent focus:outline-none text-lg"
              />
              <button
                onClick={() => sendMessage()}
                className="ml-3 bg-[#4F46E5] text-white px-5 py-2 rounded-full"
              >
                →
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {currentSuggestions.map((q, i) => (
              <button
                key={i}
                onClick={() => sendMessage(q)}
                className="px-5 py-3 bg-white rounded-full shadow text-sm hover:bg-gray-50"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      <header className="fixed top-0 w-full bg-white border-b z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo Left */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#4F46E5] flex items-center justify-center text-white font-bold text-sm">
              N
            </div>
            <span className="font-bold text-gray-900 text-base tracking-tight">NexaGPT</span>
          </div>

          {/* User Icon Right */}
          <div className="flex items-center gap-3">
            <span className="font-semibold text-gray-800 text-sm">
              Powered by NexaGPT
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pt-20 pb-52">
        <div className="max-w-5xl mx-auto px-6 space-y-4">
          {messages.map((msg, i) => {
            const isUser = msg.role === "user";
            const bgColor = !isUser ? "bg-gray-100" : "bg-[#4F46E5]";
            const textColor = !isUser ? "text-gray-900" : "text-white";
            const icon = !isUser ? "👤" : "🤖";
            const timestamp = msg.timestamp
              ? getTimeLabel(msg.timestamp)
              : "Just now";

            return (
              <div
                key={i}
                className={`flex ${
                  isUser ? "justify-end" : "justify-start"
                } items-end`}
              >
                {/* USER ICON (LEFT) */}
                {!isUser && (
                  <FaRobot
                    size={25}
                    className="text-[#4F46E5] mt-1 mr-1 flex-shrink-0"
                  />
                )}
                <div
                  className={`flex flex-col ${
                    !isUser ? "items-start" : "items-end"
                  }`}
                >
                  <div
                    className={`
                    max-w-[100%] px-4 pt-2 rounded-2xl shadow ${bgColor} ${textColor}`}
                  >
                    <p className="flex p-2">
                      <div>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => (
                              <p className="mb-2 leading-relaxed">{children}</p>
                            ),
                            ul: ({ children }) => (
                              <ul className="list-disc pl-10 pt-2 pb-2 space-y-1 marker:text-[#4F46E5]">
                                {children}
                              </ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="list-decimal pl-10 pt-2 pb-2 space-y-1 marker:text-gray-900">
                                {children}
                              </ol>
                            ),
                            li: ({ children }) => <li className="pb-2 text-gray-900">{children}</li>,
                            code: ({ inline, children }) =>
                              inline ? (
                                <code className="bg-gray-200 text-sm px-1 py-0.5 rounded">
                                  {children}
                                </code>
                              ) : (
                                <pre className="bg-black text-green-400 p-3 rounded-lg overflow-x-auto text-sm">
                                  <code>{children}</code>
                                </pre>
                              ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                      {/* <div>
{!isUser && typing && i === messages.length - 1 && (
                        <span className="ml-2 animate-pulse">✍️</span>
                      )}</div> */}
                    </p>
                  </div>
                  {/* Timestamp */}
                  <span
                    className={`block text-xs text-gray-400 mt-2 mr-2 ml-2 ${
                      !isUser ? "text-left" : "text-right"
                    }`}
                  >
                    {timestamp}
                  </span>
                </div>
                {/* ASSISTANT ICON (RIGHT) */}
                {isUser && (
                  <FaUserCircle
                    size={25}
                    className="text-gray-200 mt-1 ml-1 flex-shrink-0"
                  />
                )}
              </div>
            );
          })}
          {/* Loading indicator */}
          {loading && !typing && (
            <p className="text-sm text-gray-500 flex justify-start gap-2 items-center">
              🤖 Searching…{" "}
            </p>
          )}

          <div ref={bottomRef} />
        </div>
      </main>

      <footer className="fixed bottom-0 w-full bg-white border-t z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          {started && currentSuggestions.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {currentSuggestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(q)}
                  className="px-4 py-2 text-sm rounded-full border border-[#4F46E5] text-[#4F46E5] hover:bg-[#4F46E5] hover:text-white transition"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {/* 
          <div className="ai-input">
            <div className="flex bg-white rounded-full px-3 py-1">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Ask something..."
                className="flex-1 bg-transparent px-3 py-2 focus:outline-none"
              />
              <button
                onClick={() => sendMessage()}
                className="bg-[#4F46E5] text-white px-5 py-2 rounded-full"
              >
                Send 🚀
              </button>
            </div>
          </div> */}
          <div className="ai-input">
            <div
              className={`flex items-center bg-white rounded-full px-3 py-1 border
      ${typing || loading ? "opacity-70" : ""}
    `}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !typing && !loading && sendMessage()
                }
                placeholder="Ask something..."
                disabled={typing || loading}
                className="flex-1 bg-transparent px-3 py-2 focus:outline-none disabled:cursor-not-allowed"
              />
              {/* ml-2 mr-2 p-3 rounded-full flex items-center justify-center transition-all duration-300 */}
              <button
                onClick={() => sendMessage()}
                disabled={typing || loading || !input.trim()}
                className={`ml-3 bg-[#4F46E5] text-white px-5 py-2 rounded-full
        
        ${
          loading
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-[#4F46E5] hover:bg-[#3730A3]"
        }
      `}
              >
                {loading ? (
                  <FaSpinner className="text-white animate-spin" size={18} />
                ) : (
                  <>→</>
                )}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
